import { useState } from 'react'
import type { BatteryStrategy } from '@/lib/batterySimulation'
import type { BatteryType } from '@/lib/batterySizing'
import {
  INSTALLED_BASE_EUR,
  INSTALLED_PACK_EUR_PER_KWH,
  PLUGIN_BASE_EUR,
  PLUGIN_PACK_EUR_PER_KWH,
} from '@/lib/batterySizing'
import { PricePerKwhInput } from './PricePerKwhInput'

export const STRATEGY_LABEL: Record<BatteryStrategy, string> = {
  'self-consumption': 'Zonnestroom zelf gebruiken',
  trading: 'Slim handelen met uurprijzen',
  'self-consumption-trading': 'Automatisch: zon eerst, dan handelen',
}

const STRATEGY_EXPLANATION: Record<BatteryStrategy, string> = {
  'self-consumption':
    'Overdag bewaart de batterij de zonnestroom die je anders aan het net teruggeeft. ’s Avonds gebruik je die stroom zelf, zodat je minder stroom hoeft te kopen.',
  trading:
    'De batterij laadt op als stroom goedkoop is en ontlaadt als stroom duur is — eerst voor je eigen verbruik, de rest wordt verkocht aan het net. Dit werkt alleen met een dynamisch energiecontract (uurprijzen).',
  'self-consumption-trading':
    'Je zonnestroom heeft altijd voorrang in de batterij; wat de zon niet vult, handelt met uurprijzen. In de zomer bewaart de batterij dus vooral zonnestroom, in de winter en op grijze dagen handelt hij. Dit werkt alleen met een dynamisch energiecontract.',
}

interface ExpertFormProps {
  months: number
  onMonthsChange: (m: number) => void
  periodOptions: number[]
  availableMonths: number
  strategy: BatteryStrategy
  setStrategy: (s: BatteryStrategy) => void
  tradingShare: number
  setTradingShare: (n: number) => void
  feedInFee: number
  setFeedInFee: (n: number) => void
  supplierMarkup: number
  setSupplierMarkup: (n: number) => void
  batteryType: BatteryType
  setBatteryType: (t: BatteryType) => void
  batteryCostPerKwh: number
  setBatteryCostPerKwh: (n: number) => void
  minSoc: number
  setMinSoc: (n: number) => void
  maxSoc: number
  setMaxSoc: (n: number) => void
  inverterSel: number | null
  setInverterSel: (n: number | null) => void
  /** Peak-based hint before C-rate / type clamps; used when override is empty. */
  recommendedInverterKw: number | null
  submitLabel: string
  onSubmit: () => void
}

/** The full parameter form for the "Zelf instellen" route. */
export function ExpertForm(props: ExpertFormProps) {
  const [showExpert, setShowExpert] = useState(false)
  const inputCls = 'input h-12 w-auto py-1'
  const selectCls = `${inputCls} pr-8`

  return (
    <div className="card p-5 md:p-[30px]">
      <h3 className="text-xl font-semibold lg:text-26">Stel de berekening zelf in</h3>
      <div className="mt-5 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="field-label">Periode</span>
          <select
            className={selectCls}
            value={props.months}
            onChange={(e) => props.onMonthsChange(Number(e.target.value))}
          >
            {props.periodOptions.map((m) => (
              <option key={m} value={m}>
                {m === props.availableMonths ? `${m} maanden (aanbevolen)` : `${m} maanden`}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="field-label">Wat doet de batterij?</span>
          <select
            className={selectCls}
            value={props.strategy}
            onChange={(e) => props.setStrategy(e.target.value as BatteryStrategy)}
          >
            {(Object.keys(STRATEGY_LABEL) as BatteryStrategy[]).map((s) => (
              <option key={s} value={s}>
                {STRATEGY_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        {props.strategy === 'self-consumption-trading' && (
          <label className="flex flex-col gap-1">
            <span className="field-label">Ruimte voor handelen: max {props.tradingShare}%</span>
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              className="h-12 w-40 accent-dark-purple"
              value={props.tradingShare}
              onChange={(e) => props.setTradingShare(Number(e.target.value))}
            />
          </label>
        )}
      </div>

      <p className="mt-3 max-w-3xl text-base leading-normal text-dark-blue/60">
        {STRATEGY_EXPLANATION[props.strategy]} We rekenen met de dynamische uurprijzen van je
        easyEnergy-contract.
      </p>

      <div className="mt-4 rounded-2xl bg-beige p-4">
        <p className="text-base font-semibold text-dark-blue">De opslag van je leverancier</p>
        <p className="mt-1 max-w-3xl text-base leading-normal text-dark-blue/60">
          Bij een dynamisch contract reken je met de uurprijs plus energiebelasting, de opslag
          van je leverancier en btw. De energiebelasting is voor iedereen gelijk, maar de
          opslag verschilt per contract — je vindt hem op je contract of jaarafrekening, vaak
          als <strong className="text-dark-blue">inkoopvergoeding</strong> of{' '}
          <strong className="text-dark-blue">opslag per kWh</strong> (exclusief btw). Die
          opslag betaal je op stroom die je afneemt en gaat eraf bij teruglevering, dus hij
          maakt het verschil tussen de twee kanten groter.
        </p>
        <div className="mt-3">
          <label className="flex flex-col gap-1">
            <span className="field-label">Opslag per kWh (excl. btw)</span>
            <PricePerKwhInput
              className={`${inputCls} w-28 !bg-white`}
              value={props.supplierMarkup}
              onChange={props.setSupplierMarkup}
            />
          </label>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-beige p-4">
        <p className="text-base font-semibold text-dark-blue">Terugleverkosten</p>
        <p className="mt-1 max-w-3xl text-base leading-normal text-dark-blue/60">
          Steeds meer leveranciers rekenen kosten voor stroom die je teruglevert aan het net.
          Staat er op je contract of jaarafrekening een bedrag per teruggeleverde kWh, vul dat
          hier in: stroom die je opslaat lever je niet terug, dus die kosten bespaar je. Betaal
          je niets of weet je het niet, laat het dan op € 0 staan — dan rekenen we er niet mee.
        </p>
        <div className="mt-3">
          <label className="flex flex-col gap-1">
            <span className="field-label">Terugleverkosten per kWh</span>
            <PricePerKwhInput
              className={`${inputCls} w-28 !bg-white`}
              value={props.feedInFee}
              onChange={props.setFeedInFee}
            />
          </label>
        </div>
      </div>

      {/* Non-essential parameters live behind the expert panel */}
      <div className="mt-4">
        <button
          type="button"
          className="flex items-center gap-1.5 text-15 font-semibold text-dark-blue/60 transition-colors hover:text-dark-blue"
          onClick={() => setShowExpert((v) => !v)}
        >
          <svg
            className={`size-3.5 transition-transform ${showExpert ? 'rotate-90' : ''}`}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 3l5 5-5 5" />
          </svg>
          Expertinstellingen
        </button>
        {showExpert && (
          <div className="mt-3 space-y-4 rounded-2xl bg-beige p-4">
            <div>
              <p className="text-base font-semibold text-dark-blue">Type, kosten en omvormer</p>
              <p className="mt-1 max-w-3xl text-base leading-normal text-dark-blue/60">
                Packprijs is een richtprijs per kWh opslag: standaard €
                {INSTALLED_PACK_EUR_PER_KWH}/kWh geïnstalleerd en €{PLUGIN_PACK_EUR_PER_KWH}/kWh
                voor een plugin-batterij. Bij geïnstalleerd komt daar €
                {INSTALLED_BASE_EUR.toLocaleString('nl-NL')} installatiebasis bij; bij plugin is
                die €{PLUGIN_BASE_EUR}. Het omvormervermogen volgt per capaciteit uit je pieken
                en het C-tarief van het type — laat het veld leeg voor die automatische keuze
                {props.recommendedInverterKw != null
                  ? ` (piekbasis ${props.recommendedInverterKw} kW)`
                  : ''}
                .
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1">
                  <span className="field-label">Batterijtype</span>
                  <select
                    className={`${selectCls} !bg-white`}
                    value={props.batteryType}
                    onChange={(e) => props.setBatteryType(e.target.value as BatteryType)}
                  >
                    <option value="installed">Geïnstalleerde batterij</option>
                    <option value="plugin">Plugin-batterij</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="field-label">Packprijs €/kWh</span>
                  <input
                    type="number"
                    step="50"
                    min="0"
                    className={`${inputCls} w-28 !bg-white`}
                    value={props.batteryCostPerKwh}
                    onChange={(e) => props.setBatteryCostPerKwh(Number(e.target.value))}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="field-label">Omvormer (kW)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="20"
                    placeholder="automatisch"
                    className={`${inputCls} w-32 !bg-white`}
                    value={props.inverterSel ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value
                      props.setInverterSel(
                        raw === '' ? null : Math.max(0.5, Math.min(20, Number(raw) || 0.5))
                      )
                    }}
                  />
                </label>
              </div>
            </div>
            <div>
              <p className="text-base font-semibold text-dark-blue">Laadvenster van de batterij</p>
              <p className="mt-1 max-w-3xl text-base leading-normal text-dark-blue/60">
                Veel batterijen houden een reserve aan die nooit wordt ontladen (bijvoorbeeld 20%)
                en laden ter bescherming van de levensduur niet helemaal vol (bijvoorbeeld tot
                90%). De berekening gebruikt alleen het deel tussen deze twee grenzen.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1">
                  <span className="field-label">Minimaal laadniveau (%)</span>
                  <input
                    type="number"
                    step="5"
                    min="0"
                    max="50"
                    className={`${inputCls} w-24 !bg-white`}
                    value={props.minSoc}
                    onChange={(e) =>
                      props.setMinSoc(Math.max(0, Math.min(50, Number(e.target.value) || 0)))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="field-label">Maximaal laadniveau (%)</span>
                  <input
                    type="number"
                    step="5"
                    min="50"
                    max="100"
                    className={`${inputCls} w-24 !bg-white`}
                    value={props.maxSoc}
                    onChange={(e) =>
                      props.setMaxSoc(Math.max(50, Math.min(100, Number(e.target.value) || 100)))
                    }
                  />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-beige-2 pt-5">
        <button className="btn-primary !h-14 !px-8 !text-base font-semibold" onClick={props.onSubmit}>
          {props.submitLabel}
        </button>
      </div>
    </div>
  )
}
